// Общий top-bar Orbis: слева «Orbis · OMA», в центре поиск, справа — спокойный статус, режим, пауза,
// уведомления, тема, профиль и выход. Меню сервиса — в боковой панели (Sidebar).
import { useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { api } from '../api.ts'
import { logout, useAuth } from '../auth.ts'
import { formatUsd } from '../lib/format.ts'
import { enableNotifications, setNotificationsEnabled, useNotifyState } from '../notify.ts'
import { Link } from '../router.tsx'
import { setSettings, useLive } from '../store.ts'
import { toggleTheme, useThemePref } from '../theme.ts'
import { toast, toastError } from '../toast.ts'
import { SAFETY_MODE_LABELS, agentTime, tzLabel, type Settings } from '../types.ts'
import { monogram } from './AgentAvatar.tsx'
import { GlobalSearch } from './GlobalSearch.tsx'
import { Icon } from './Icon.tsx'
import { cx } from './ui.tsx'

export function OrbisMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true" focusable="false">
      <circle cx="16" cy="16" r="6" fill="var(--orbis-brand)" />
      <ellipse
        cx="16"
        cy="16"
        rx="14"
        ry="6.5"
        fill="none"
        stroke="var(--orbis-brand-2)"
        strokeWidth="2"
        transform="rotate(-28 16 16)"
      />
      <circle cx="27.5" cy="10.5" r="2.6" fill="var(--orbis-brand-2)" />
    </svg>
  )
}

/** Текущее время с точностью до минуты: пересчёт ровно на смене минуты. */
function useMinute(): number {
  const [t, setT] = useState(() => Date.now())
  useEffect(() => {
    let timer = 0
    const arm = () => {
      timer = window.setTimeout(() => {
        setT(Date.now())
        arm()
      }, 60_000 - (Date.now() % 60_000) + 50)
    }
    arm()
    return () => window.clearTimeout(timer)
  }, [])
  return t
}

/** Часы агентов: по ним OMA называет агентам время, по ним же — сроки «ждёт до 14:30 МСК» на карточках. */
export function AgentClock({ className }: { className?: string }) {
  const tz = useLive((s) => s.settings?.agent_timezone)
  const now = useMinute()
  if (!tz) return null
  const label = tzLabel(tz)
  return (
    <span
      // display задаёт className (hidden / inline-flex по ширине экрана): inline-flex здесь же перебивал бы hidden
      className={cx('tnum items-center gap-1 text-[12.5px] whitespace-nowrap text-muted', className || 'inline-flex')}
      title={`Время агентов (${label}). Все сроки ожиданий в OMA — «ждёт до 14:30 ${label}» — по этим часам, где бы вы ни находились. Меняется в настройках.`}
      aria-label={`Время агентов: ${agentTime(now, tz)}`}
    >
      <Icon name="clock" size={14} className="text-subtle" />
      <b className="font-semibold text-fg-2">{agentTime(now, tz, { label: false })}</b>
      <span>{label}</span>
    </span>
  )
}

/** Отдельно показываем занятых агентов и запуски: один агент может занимать несколько мест. */
export function LiveSummary({ className, wrap }: { className?: string; wrap?: boolean }) {
  const stats = useLive((s) => s.stats)
  const settings = useLive((s) => s.settings)
  const busy = stats?.agents_busy ?? 0
  const queued = stats?.jobs_queued ?? 0
  const waiting = (stats?.tasks_by_status.waiting_agents ?? 0) + (stats?.tasks_by_status.waiting_human ?? 0)
  const nextWake = stats?.next_wake_at ?? null
  const tz = settings?.agent_timezone
  const cost = stats?.cost_today_usd ?? 0
  const budget = settings?.daily_budget_usd ?? 0
  const over = budget > 0 && cost >= budget
  const near = budget > 0 && !over && cost >= budget * 0.8
  return (
    <span className={cx('tnum text-[12.5px] text-muted', wrap ? 'whitespace-normal' : 'whitespace-nowrap', className)} title={`Запусков сейчас: ${stats?.jobs_running ?? 0}`}>
      агентов <b className="font-semibold text-fg-2">{busy}</b>
      <span className="px-1.5 text-subtle">·</span>запусков <b className="font-semibold text-fg-2">{stats?.jobs_running ?? 0}</b>
      <span className="px-1.5 text-subtle">·</span>в очереди <b className="font-semibold text-fg-2">{queued}</b>
      {waiting ? (
        <span
          title={`Задачи ждут подзадач, ответов коллег, вашего решения или назначенного времени — агенты вернутся к ним сами${nextWake ? `. Ближайшее возвращение — в ${agentTime(nextWake, tz)}` : ''}`}
        >
          <span className="px-1.5 text-subtle">·</span>ждут <b className="font-semibold text-fg-2">{waiting}</b>
          {nextWake ? <span className="text-subtle"> (ближайшая в {agentTime(nextWake, tz, { label: false })})</span> : null}
        </span>
      ) : null}
      <span className="px-1.5 text-subtle">·</span>сегодня{' '}
      <b className={cx('font-semibold', over ? 'text-danger' : near ? 'text-warn' : 'text-fg-2')}>{formatUsd(cost)}</b>
      {budget > 0 ? <> из {formatUsd(budget)}</> : null}
    </span>
  )
}

export function SafetyBadge({ className }: { className?: string }) {
  const mode = useLive((s) => s.settings?.safety_mode)
  if (!mode) return null
  return (
    <Link
      to="/settings"
      className={cx('stbadge hover:border-line-strong', mode !== 'read_only' && 'warn', className)}
      title="Режим безопасности агентов — настройки"
    >
      <Icon name="shield" size={13} />
      {SAFETY_MODE_LABELS[mode]}
    </Link>
  )
}

export function usePauseToggle(): { paused: boolean; busy: boolean; toggle: () => Promise<void>; ready: boolean } {
  const settings = useLive((s) => s.settings)
  const [busy, setBusy] = useState(false)
  const toggle = async () => {
    if (!settings || busy) return
    setBusy(true)
    try {
      const r = await api.patch<{ settings: Settings }>('/api/settings', { paused: !settings.paused })
      if (r?.settings) setSettings(r.settings)
      toast(r?.settings?.paused ? 'Глобальная пауза: новые запуски не стартуют' : 'Пауза снята — агенты снова работают', 'success')
    } catch (e) {
      toastError(e, 'Не удалось переключить паузу')
    } finally {
      setBusy(false)
    }
  }
  return { paused: settings?.paused ?? false, busy, toggle, ready: Boolean(settings) }
}

function NotifyButton() {
  const state = useNotifyState()
  const openEsc = useLive((s) => s.stats?.open_escalations ?? 0)
  if (state === 'unsupported') return null
  const label =
    state === 'on'
      ? 'Уведомления о вопросах агентов включены — выключить'
      : state === 'off'
        ? 'Уведомления выключены — включить'
        : state === 'denied'
          ? 'Уведомления запрещены в настройках браузера'
          : 'Включить уведомления о вопросах агентов'
  return (
    <button
      type="button"
      className="iconbtn"
      aria-label={label}
      title={label}
      aria-pressed={state === 'on'}
      onClick={() => {
        if (state === 'on') {
          setNotificationsEnabled(false)
          toast('Уведомления выключены', 'info')
        } else if (state === 'off') {
          setNotificationsEnabled(true)
          toast('Уведомления включены: вопросы агентов придут, даже когда вкладка в фоне', 'success')
        } else if (state === 'default') {
          void enableNotifications().then((p) =>
            toast(
              p === 'granted' ? 'Уведомления включены: вопросы агентов придут, даже когда вкладка в фоне' : 'Уведомления не разрешены браузером',
              p === 'granted' ? 'success' : 'error',
            ),
          )
        } else {
          toast('Браузер запретил уведомления для этого сайта — разреши их в настройках сайта', 'info')
        }
      }}
    >
      <Icon name={state === 'off' || state === 'denied' ? 'bellOff' : 'bell'} size={17} />
      {openEsc > 0 ? <span className="dotb" aria-hidden="true" /> : null}
    </button>
  )
}

/** Кнопка темы: в top-bar на широких экранах, в меню — на телефоне. */
export function ThemeButton({ className }: { className?: string }) {
  const theme = useThemePref()
  return (
    <button
      type="button"
      className={cx('iconbtn', className)}
      aria-label={theme === 'dark' ? 'Тёмная тема — переключить на светлую' : 'Светлая тема — переключить на тёмную'}
      title={theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
      onClick={toggleTheme}
    >
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={17} />
    </button>
  )
}

export function LogoutButton({ className }: { className?: string }) {
  return (
    <button type="button" className={cx('iconbtn', className)} aria-label="Выйти" title="Выйти" onClick={() => void logout()}>
      <Icon name="logout" size={17} />
    </button>
  )
}

export function Topbar({ menuOpen, onMenu }: { menuOpen: boolean; onMenu: () => void }) {
  const { user } = useAuth()
  const pause = usePauseToggle()
  const name = user?.name || user?.email || ''
  // поиск на узком экране: кнопка-лупа раскрывает поле поверх top-bar
  const [searchOpen, setSearchOpen] = useState(false)
  return (
    <header className="topbar">
      <button
        type="button"
        className="iconbtn min-[900px]:hidden"
        aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'}
        aria-expanded={menuOpen}
        onClick={onMenu}
      >
        <Icon name={menuOpen ? 'x' : 'menu'} size={18} />
      </button>
      <Link to="/" className="svcswitch" aria-label="Orbis · OMA — на доску">
        <OrbisMark className="mark" />
        <span className="wm">Orbis</span>
        <span className="sep" aria-hidden="true" />
        <span className="svc">OMA</span>
      </Link>

      <div className="hidden min-w-0 flex-1 justify-center min-[720px]:flex">
        <GlobalSearch />
      </div>
      <div className="flex-1 min-[720px]:hidden" />

      {/* на телефоне (уже 640px) тема, профиль и выход — в меню, чтобы поиск, часы и пауза помещались */}
      <div className="flex shrink-0 items-center gap-2 max-[520px]:gap-1.5">
        <button
          type="button"
          className="iconbtn min-[720px]:hidden"
          aria-label="Поиск задач и агентов"
          title="Поиск"
          // синхронно, чтобы iOS открыл клавиатуру: фокус должен случиться внутри нажатия
          onClick={() => flushSync(() => setSearchOpen(true))}
        >
          <Icon name="search" size={17} />
        </button>
        <AgentClock className="hidden min-[380px]:inline-flex" />
        <LiveSummary className="hidden min-[1440px]:inline" />
        <SafetyBadge className="hidden min-[1000px]:inline-flex" />
        {pause.ready ? (
          <button
            type="button"
            onClick={() => void pause.toggle()}
            disabled={pause.busy}
            aria-pressed={pause.paused}
            title={pause.paused ? 'Снять глобальную паузу' : 'Глобальная пауза: новые запуски не стартуют'}
            className={cx(
              'inline-flex h-8 items-center gap-1.5 rounded-ctl border px-2.5 text-[13px] font-semibold transition-colors disabled:opacity-50',
              pause.paused
                ? 'border-transparent bg-warn-soft text-warn hover:brightness-[0.98]'
                : 'border-line-strong bg-surface text-fg-2 hover:bg-surface-2',
            )}
          >
            <Icon name={pause.paused ? 'play' : 'pause'} size={14} />
            <span className="hidden min-[720px]:inline">{pause.paused ? 'Продолжить' : 'Пауза'}</span>
          </button>
        ) : null}
        <NotifyButton />
        <ThemeButton className="hidden min-[640px]:inline-grid" />
        {user ? (
          <span className="me max-[640px]:hidden" title={user.email}>
            <span className="av" aria-hidden="true">
              {monogram(name)}
            </span>
            <span className="who">
              <span className="nm block max-w-[140px] truncate">{name}</span>
              <span className="rl block">{user.role === 'admin' ? 'владелец' : user.role}</span>
            </span>
          </span>
        ) : null}
        <LogoutButton className="hidden min-[640px]:inline-grid" />
      </div>

      {searchOpen ? (
        <div className="absolute inset-0 z-50 flex items-center gap-1.5 bg-surface px-2 min-[720px]:hidden">
          <GlobalSearch autoFocus onDone={() => setSearchOpen(false)} />
          <button type="button" className="iconbtn" aria-label="Закрыть поиск" title="Закрыть поиск" onClick={() => setSearchOpen(false)}>
            <Icon name="x" size={18} />
          </button>
        </div>
      ) : null}
    </header>
  )
}
